import castToBoolean from "./castToBoolean";

export type MaybeUpdateComparer<T> = (a?: T | null, b?: T | null) => boolean;

export function defaultComparer<T extends string>(a?: T | null, b?: T | null) {
  return a == b;
}

/**
 * If the value needs to be updated, it updates it.
 */
export function maybeUpdateStringValue({
  subject,
  key,
  value,
  comparer = defaultComparer,
}: {
  subject: unknown;
  key: string;
  value?: string | null;
  comparer?: MaybeUpdateComparer<string>;
}): { subject: unknown; updated: boolean } {
  if (value === undefined) {
    return { subject: subject, updated: false };
  }

  if (comparer((subject as Record<string, string | null>)[key], value)) {
    return { subject: subject, updated: false };
  }

  (subject as Record<string, string | null>)[key] = value;
  return { subject: subject, updated: true };
}

/**
 * If the value needs to be updated, it updates it.
 */
export function maybeUpdateBooleanValue({
  subject,
  key,
  value,
}: {
  subject: unknown;
  key: string;
  value?: boolean;
}): { subject: unknown; updated: boolean } {
  if (value === undefined) {
    return { subject: subject, updated: false };
  }

  if (castToBoolean((subject as Record<string, string>)[key]) == value) {
    return { subject: subject, updated: false };
  }

  (subject as Record<string, boolean>)[key] = value;
  return { subject: subject, updated: true };
}

/**
 * If the value needs to be updated, it updates it
 *
 * You may pass `null` as in the `value` parameter to
 * clear the value in the subject. `undefined` will leave the
 * value as-is. These two values should represent the lack of
 * a value, and empty string as a Date value respectively.
 */
export function maybeUpdateDateValue({
  subject,
  key,
  value,
}: {
  subject: unknown;
  key: string;
  value?: Date | null;
}): { subject: unknown; updated: boolean } {
  // Warning: Do not remove this explicit `undefined` check here.
  // `null` is a special case used to clear out the Date value, if you
  // replace this for a coercion to boolean (E.G. `!value`) you will remove
  // the ability for the user to clear out the date value, as we
  // are encoding the presence of an empty string for a Date value as `null`.
  if (value === undefined) {
    return { subject: subject, updated: false };
  }

  if ((subject as Record<string, Date>)[key]?.getTime() === value?.getTime()) {
    return { subject: subject, updated: false };
  }
  (subject as Record<string, Date | null>)[key] = value;
  return { subject: subject, updated: true };
}

/**
 * If the value needs to be updated, it updates it
 */
export function maybeUpdateNumberValue({
  subject,
  key,
  value,
}: {
  subject: unknown;
  key: string;
  value?: number;
}): { subject: unknown; updated: boolean } {
  if (value == undefined) {
    return { subject: subject, updated: false };
  }

  if ((subject as Record<string, number>)[key] == value) {
    return { subject: subject, updated: false };
  }

  (subject as Record<string, number>)[key] = value;
  return { subject: subject, updated: true };
}
